import { Autocomplete, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import findAllPlacesWithIqLocation from "../../resources/Api/location/findAllPlacesWithIqLocation";

type Place = { display_name: string; lat: string | number; lon: string | number };

export type RouteAddress = {
    text: string;
    coordinates?: { lat: number; lng: number };
};

type RouteAddressAutocompleteProps = {
    label: string;
    placeholder: string;
    address: RouteAddress;
    onChange: (address: RouteAddress) => void;
    onSearchError: () => void;
    disabled?: boolean;
    sx?: object;
};

const RouteAddressAutocomplete = ({
    label, placeholder, address, onChange, onSearchError, disabled, sx,
}: RouteAddressAutocompleteProps) => {
    const [options, setOptions] = useState<Place[]>([]);
    const [loading, setLoading] = useState(false);
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const requestNumber = useRef(0);

    useEffect(() => () => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        requestNumber.current += 1;
    }, []);

    useEffect(() => {
        if (address.text) return;
        if (searchTimer.current) clearTimeout(searchTimer.current);
        requestNumber.current += 1;
        setOptions([]);
        setLoading(false);
    }, [address.text]);

    const searchAddress = (text: string) => {
        onChange({ text });
        setOptions([]);
        setLoading(false);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        const currentRequest = ++requestNumber.current;
        if (text.trim().length < 3) return;
        searchTimer.current = setTimeout(async () => {
            setLoading(true);
            try {
                const response = await findAllPlacesWithIqLocation(text.trim());
                if (currentRequest === requestNumber.current) {
                    setOptions(Array.isArray(response.data) ? response.data : []);
                }
            } catch {
                if (currentRequest === requestNumber.current) onSearchError();
            } finally {
                if (currentRequest === requestNumber.current) setLoading(false);
            }
        }, 500);
    };

    const selectPlace = (place: Place | string | null) => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        requestNumber.current += 1;
        setLoading(false);
        setOptions([]);
        if (!place) {
            onChange({ text: "" });
        } else if (typeof place === "string") {
            onChange({ text: place });
        } else {
            const lat = Number(place.lat);
            const lng = Number(place.lon);
            onChange({
                text: place.display_name,
                coordinates: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : undefined,
            });
        }
    };

    return <Autocomplete<Place, false, false, true>
        freeSolo
        loading={loading}
        loadingText="Buscando endereços..."
        noOptionsText={address.text.trim().length >= 3 ? "Nenhum endereço encontrado" : "Digite pelo menos 3 caracteres"}
        options={options}
        getOptionLabel={(option) => typeof option === "string" ? option : option.display_name}
        inputValue={address.text}
        filterOptions={(items) => items}
        onInputChange={(_, text, reason) => { if (reason === "input") searchAddress(text); }}
        onChange={(_, place) => selectPlace(place)}
        disabled={disabled}
        sx={{ minWidth: 0, ...sx }}
        renderInput={(params) => <TextField {...params} fullWidth label={label} placeholder={placeholder} InputLabelProps={{ shrink: true }} />}
    />;
};

export default RouteAddressAutocomplete;
